/**
 * @file     useSnapTrack.ts
 * @purpose  The index of a native horizontal scroll-snap photo track. The
 *           browser owns the swipe (finger tracking, momentum, rubber band,
 *           all on the compositor); this reads the live slide from scrollLeft
 *           once per frame, reports the settled slide, glides to a slide on
 *           arrows and keys, and holds the slide through resizes.
 */
'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/** Fallback for browsers without `scrollend`: the track counts as settled after this quiet gap. */
const SETTLE_MS = 140;

/** RTL tracks scroll toward negative scrollLeft. */
const dirSign = (el: HTMLElement) => (getComputedStyle(el).direction === 'rtl' ? -1 : 1);

interface Options {
  /** Slides in the track, end cards included. */
  count: number;
  initialIndex?: number;
  /** The track's CSS column gap in px, so the slide pitch is width + gap. */
  gap?: number;
  /** Jumps instead of gliding (prefers-reduced-motion). */
  reducedMotion?: boolean;
  /** The slide the track came to rest on, and every programmatic move. */
  onSettle?: (index: number) => void;
}

export function useSnapTrack({ count, initialIndex = 0, gap = 0, reducedMotion = false, onSettle }: Options) {
  const clamp = useCallback((i: number) => Math.min(Math.max(i, 0), Math.max(count - 1, 0)), [count]);
  const [index, setIndexState] = useState(() => clamp(initialIndex));
  const [track, setTrack] = useState<HTMLElement | null>(null);
  const indexRef = useRef(index);
  const settledRef = useRef(index);
  // While a programmatic glide runs, the slides it passes are not the visitor's.
  const targetRef = useRef<number | null>(null);
  const onSettleRef = useRef(onSettle);
  useEffect(() => {
    onSettleRef.current = onSettle;
  }, [onSettle]);

  const setIndex = useCallback((i: number) => {
    indexRef.current = i;
    setIndexState(i);
  }, []);

  const pitch = useCallback((el: HTMLElement) => el.clientWidth + gap, [gap]);

  const ref = useCallback(
    (el: HTMLElement | null) => {
      // Mounting (or remounting after a tab switch) lands on the slide before paint.
      if (el) {
        const p = el.clientWidth + gap;
        if (p > gap) el.scrollLeft = dirSign(el) * indexRef.current * p;
      }
      setTrack(el);
    },
    [gap],
  );

  useEffect(() => {
    if (!track) return;
    let frame = 0;
    let timer = 0;
    const sign = dirSign(track);

    const read = () => {
      const p = pitch(track);
      return p > gap ? clamp(Math.round((sign * track.scrollLeft) / p)) : indexRef.current;
    };

    const settle = () => {
      window.clearTimeout(timer);
      targetRef.current = null;
      const i = read();
      if (i !== indexRef.current) setIndex(i);
      if (i !== settledRef.current) {
        settledRef.current = i;
        onSettleRef.current?.(i);
      }
    };

    const onScroll = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(settle, SETTLE_MS);
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        if (targetRef.current !== null) return;
        const i = read();
        if (i !== indexRef.current) setIndex(i);
      });
    };

    // A touch or wheel during a glide hands the track back to the visitor.
    const release = () => {
      targetRef.current = null;
    };

    track.addEventListener('scroll', onScroll, { passive: true });
    track.addEventListener('scrollend', settle);
    track.addEventListener('touchstart', release, { passive: true });
    track.addEventListener('wheel', release, { passive: true });

    let resize: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resize = new ResizeObserver(() => {
        const p = pitch(track);
        const left = sign * indexRef.current * p;
        if (p <= gap || Math.abs(track.scrollLeft - left) < 1) return;
        targetRef.current = indexRef.current;
        track.scrollTo({ left, behavior: 'auto' });
      });
      resize.observe(track);
    }

    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
      track.removeEventListener('scroll', onScroll);
      track.removeEventListener('scrollend', settle);
      track.removeEventListener('touchstart', release);
      track.removeEventListener('wheel', release);
      resize?.disconnect();
    };
  }, [track, pitch, clamp, gap, setIndex]);

  const scrollToIndex = useCallback(
    (n: number) => {
      const i = clamp(n);
      const from = indexRef.current;
      setIndex(i);
      if (i !== settledRef.current) {
        settledRef.current = i;
        onSettleRef.current?.(i);
      }
      if (!track) return;
      const p = pitch(track);
      if (p <= gap) return;
      targetRef.current = i;
      // Neighbours glide; far jumps (thumbnails, Home, End) cut, as iOS Photos does.
      const glide = !reducedMotion && Math.abs(i - from) <= 1;
      track.scrollTo?.({ left: dirSign(track) * i * p, behavior: glide ? 'smooth' : 'auto' });
    },
    [clamp, track, pitch, gap, reducedMotion, setIndex],
  );

  return { ref, index, scrollToIndex };
}
